import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AdminShell from "../../Components/admin/AdminShell";
import "../Events.css";
import "./adminPosts.css";
import {
  deleteEvent,
  fetchEventsForAdmin,
  resetEventsToDefaults,
  usesRemoteEventsApi,
} from "../../services/eventsApi";

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return iso;
  }
}

function initials(name) {
  return (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");
}

export default function AdminPosts() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchEventsForAdmin();
      setItems(data || []);
    } catch (e) {
      setError("Failed to load events.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const featured = useMemo(() => items.find((x) => x.featured) || items[0], [items]);
  const rest = useMemo(() => items.filter((x) => x !== featured), [items, featured]);

  const onDelete = async (id, title) => {
    const ok = window.confirm(`Delete "${title.slice(0, 60)}${title.length > 60 ? "…" : ""}"? This cannot be undone.`);
    if (!ok) return;
    try {
      const removed = await deleteEvent(id);
      if (removed) await load();
    } catch (e) {
      setError("Failed to delete event.");
    }
  };

  return (
    <AdminShell title="Posts" subtitle="Manage events shown on the public Events page">
      <div className="eventsPage adminPostsPage">
        <div className="eventsWrap">
          <h1 className="eventsTitle">Events</h1>

          {error ? (
            <div className="eventsError">
              <div className="eventsErrorTitle">Something went wrong</div>
              <div className="eventsErrorMsg">{error}</div>
              <button className="eventsBtn" type="button" onClick={() => load()}>
                Retry
              </button>
            </div>
          ) : null}

          {!loading && items.length === 0 ? (
            <div className="adminPostsEmpty">
              <div className="adminPostsEmptyTitle">No events yet</div>
              <div>
                {usesRemoteEventsApi()
                  ? "Create events in your CMS or API — they will appear here."
                  : "Create a post or restore data from the site defaults."}
              </div>
              <div className="adminEventActions" style={{ justifyContent: "center", marginTop: 16 }}>
                <Link className="adminEventBtn" to="/admin/articles/new">
                  Create post
                </Link>
                {!usesRemoteEventsApi() ? (
                  <button
                    type="button"
                    className="adminEventBtn"
                    onClick={() => {
                      resetEventsToDefaults();
                      load();
                    }}
                  >
                    Restore defaults
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}

          {loading ? (
            <div className="eventsFeatured eventsFeaturedLoading" aria-hidden="true">
              <div className="eventsSkelImg" />
              <div className="eventsSkelRight">
                <div className="eventsSkelLine w40" />
                <div className="eventsSkelLine w90" />
                <div className="eventsSkelLine w85" />
                <div className="eventsSkelLine w80" />
                <div className="eventsSkelLine w55" style={{ marginTop: 14 }} />
              </div>
            </div>
          ) : featured ? (
            <div className="eventsFeatured">
              <div className="eventsFeaturedImg">
                <img src={featured.imageUrl} alt={featured.title} />
              </div>
              <div className="eventsFeaturedBody">
                <div className="eventsMeta">
                  <span className="eventsMetaStrong">
                    {featured.category}, {featured.tag}
                  </span>
                  <span className="eventsDot">—</span>
                  <span>{formatDate(featured.date)}</span>
                </div>
                <h2 className="eventsFeaturedHeadline">{featured.title}</h2>
                <p className="eventsExcerpt">{featured.excerpt}</p>
                <div className="adminEventActions">
                  <Link className="adminEventBtn" to={`/admin/posts/${featured.id}/edit`}>
                    Edit
                  </Link>
                  <button type="button" className="adminEventBtn adminEventBtnDanger" onClick={() => onDelete(featured.id, featured.title)}>
                    Delete
                  </button>
                  <Link className="adminEventBtn" to={`/Events/${featured.id}`} target="_blank" rel="noreferrer">
                    View public
                  </Link>
                </div>
                <div className="eventsAuthor">
                  <div className="eventsAvatar" aria-hidden="true">
                    {initials(featured.author?.name)}
                  </div>
                  <div>
                    <div className="eventsAuthorName">{featured.author?.name}</div>
                    <div className="eventsAuthorRole">{featured.author?.role}</div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          <div className="eventsPager" aria-hidden="true">
            <span className="eventsPagerDot active" />
            <span className="eventsPagerDot" />
            <span className="eventsPagerDot" />
            <span className="eventsPagerDot" />
          </div>

          <div className="eventsGrid">
            {loading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="eventsCard eventsCardLoading" aria-hidden="true">
                    <div className="eventsSkelCardImg" />
                    <div className="eventsCardBody">
                      <div className="eventsSkelLine w35" />
                      <div className="eventsSkelLine w95" />
                      <div className="eventsSkelLine w90" />
                      <div className="eventsSkelLine w70" />
                      <div className="eventsSkelLine w55" style={{ marginTop: 10 }} />
                    </div>
                  </div>
                ))
              : rest.map((ev) => (
                  <article key={ev.id} className="eventsCard">
                    <div className="eventsCardImg">
                      <img src={ev.imageUrl} alt={ev.title} />
                    </div>
                    <div className="eventsCardBody">
                      <div className="eventsMeta small">
                        <span className="eventsMetaStrong">
                          {ev.category}, {ev.tag}
                        </span>
                        <span className="eventsDot">—</span>
                        <span>{formatDate(ev.date)}</span>
                      </div>
                      <div className="eventsCardTitle">{ev.title}</div>
                      <p className="eventsCardExcerpt">{ev.excerpt}</p>
                      <div className="adminEventActions compact">
                        <button type="button" className="adminEventBtn" onClick={() => navigate(`/admin/posts/${ev.id}/edit`)}>
                          Edit
                        </button>
                        <button type="button" className="adminEventBtn adminEventBtnDanger" onClick={() => onDelete(ev.id, ev.title)}>
                          Delete
                        </button>
                        <Link className="adminEventBtn" to={`/Events/${ev.id}`} target="_blank" rel="noreferrer">
                          View
                        </Link>
                      </div>
                      <div className="eventsAuthor small">
                        <div className="eventsAvatar" aria-hidden="true">
                          {initials(ev.author?.name)}
                        </div>
                        <div>
                          <div className="eventsAuthorName">{ev.author?.name}</div>
                          <div className="eventsAuthorRole">{ev.author?.role}</div>
                        </div>
                      </div>
                    </div>
                  </article>
                ))}
          </div>
        </div>
      </div>
    </AdminShell>
  );
}
